/**
 * CrisisResourcesModal Component
 *
 * Full-screen modal displaying crisis resources with tap-to-call functionality.
 * Opens from CrisisBanner or can be triggered programmatically.
 *
 * Resources Displayed:
 * 1. 988 Suicide & Crisis Lifeline (US) - tap-to-call
 * 2. Crisis Text Line - text HOME to 741741
 * 3. International resources link
 * 4. "I'm Concerned" flag button (future: Week 2)
 *
 * Design Principles:
 * - CLEAR: Large, easy-to-read text
 * - ACTIONABLE: One-tap access to help
 * - ACCESSIBLE: WCAG AAA compliant
 * - NON-JUDGMENTAL: Supportive, compassionate tone
 *
 * Accessibility:
 * - 56px minimum touch targets for crisis CTAs
 * - High contrast colors (7:1+ ratio)
 * - Screen reader support
 * - Semantic link roles
 *
 * Created: 2025-12-18
 * Phase: Moderation & Safety Infrastructure (Week 1)
 */

import {
  View,
  Text,
  Pressable,
  Modal,
  Linking,
  Platform,
  StyleSheet,
  ScrollView,
  Alert,
} from 'react-native';
import { useTranslation } from '@dramadetector/shared';
import { captureEvent } from '../../lib/analytics';

export interface CrisisResourcesModalProps {
  /**
   * Whether the modal is visible
   */
  visible: boolean;

  /**
   * Severity level from crisis detection
   */
  severity?: 'low' | 'medium' | 'high' | 'critical';

  /**
   * Whether this was triggered by crisis detection (vs user-initiated)
   */
  crisisTriggered?: boolean;

  /**
   * Callback when modal is closed
   */
  onClose: () => void;

  /**
   * Callback when a resource is accessed
   */
  onResourceAccessed?: (resourceType: string) => void;
}

/**
 * Crisis resource definitions
 * Note: These are US-focused for MVP; international expansion in Week 3
 */
const CRISIS_RESOURCES = {
  phone_988: {
    name: '988 Suicide & Crisis Lifeline',
    description: 'Free, confidential support 24/7',
    phone: '988',
    phoneUrl: 'tel:988',
    icon: '📞',
    resourceType: '988_call',
  },
  text_741741: {
    name: 'Crisis Text Line',
    description: 'Text HOME to 741741',
    smsUrl: 'sms:741741?body=HOME',
    icon: '💬',
    resourceType: 'crisis_text_line',
  },
  international: {
    name: 'International Resources',
    description: 'Find help in your country',
    webUrl: 'https://findahelpline.com/',
    icon: '🌍',
    resourceType: 'international_resources',
  },
} as const;

/**
 * CrisisResourcesModal - Full resources modal with tap-to-call
 */
export function CrisisResourcesModal({
  visible,
  severity = 'low',
  crisisTriggered = false,
  onClose,
  onResourceAccessed,
}: CrisisResourcesModalProps) {
  const { t } = useTranslation('safety');

  // Handle phone call (988)
  const handlePhoneCall = async () => {
    const resource = CRISIS_RESOURCES.phone_988;

    try {
      const canOpen = await Linking.canOpenURL(resource.phoneUrl);

      if (canOpen) {
        await Linking.openURL(resource.phoneUrl);
        onResourceAccessed?.(resource.resourceType);
      } else {
        // Fallback: Show number to copy
        Alert.alert(
          t('modal.call_fallback_title'),
          t('modal.call_fallback_message', { phone: resource.phone }),
          [{ text: t('modal.ok'), style: 'default' }]
        );
      }
    } catch (error) {
      console.error('[CrisisResourcesModal] Failed to open phone:', error);
      Alert.alert(
        t('modal.call_fallback_title'),
        t('modal.call_fallback_message', { phone: resource.phone }),
        [{ text: t('modal.ok'), style: 'default' }]
      );
    }
  };

  // Handle text message (741741)
  const handleTextMessage = async () => {
    const resource = CRISIS_RESOURCES.text_741741;

    try {
      const canOpen = await Linking.canOpenURL(resource.smsUrl);

      if (canOpen) {
        await Linking.openURL(resource.smsUrl);
        onResourceAccessed?.(resource.resourceType);
      } else {
        // Fallback: Show instructions
        Alert.alert(
          t('modal.sms_fallback_title'),
          t('modal.sms_fallback_message'),
          [{ text: t('modal.ok'), style: 'default' }]
        );
      }
    } catch (error) {
      console.error('[CrisisResourcesModal] Failed to open SMS:', error);
      Alert.alert(
        t('modal.sms_fallback_title'),
        t('modal.sms_fallback_message'),
        [{ text: t('modal.ok'), style: 'default' }]
      );
    }
  };

  // Handle international resources link
  const handleInternationalLink = async () => {
    const resource = CRISIS_RESOURCES.international;

    try {
      await Linking.openURL(resource.webUrl);
      onResourceAccessed?.(resource.resourceType);
    } catch (error) {
      console.error('[CrisisResourcesModal] Failed to open web URL:', error);
    }
  };

  // Determine header styling based on severity
  const headerStyle =
    severity === 'high' || severity === 'critical'
      ? styles.headerUrgent
      : styles.headerDefault;

  return (
    <Modal
      visible={visible}
      transparent
      animationType="slide"
      onRequestClose={onClose}
    >
      {/* Dark overlay */}
      <View style={styles.overlay}>
        {/* Modal content */}
        <View style={styles.modalContainer}>
          <ScrollView
            style={styles.scrollView}
            contentContainerStyle={styles.scrollContent}
            showsVerticalScrollIndicator={false}
          >
            {/* Header */}
            <View style={[styles.header, headerStyle]}>
              <Text style={styles.headerIcon}>💙</Text>
              <Text style={styles.headerTitle}>
                {crisisTriggered
                  ? t('modal.title_triggered')
                  : t('modal.title_default')}
              </Text>
              <Text style={styles.headerSubtitle}>
                {t('modal.subtitle')}
              </Text>
            </View>

            {/* Resources Section */}
            <View style={styles.resourcesSection}>
              {/* 988 Call Button - Primary CTA */}
              <Pressable
                onPress={handlePhoneCall}
                style={styles.resourceButtonPrimary}
                accessibilityRole="button"
                accessibilityLabel={t('modal.call_988_accessibility')}
                accessibilityHint={t('modal.call_988_hint')}
                testID="crisis-call-988"
              >
                <Text style={styles.resourceIcon}>
                  {CRISIS_RESOURCES.phone_988.icon}
                </Text>
                <View style={styles.resourceText}>
                  <Text style={styles.resourceName}>
                    {t('modal.resource_988_name')}
                  </Text>
                  <Text style={styles.resourceDescription}>
                    {t('modal.resource_988_description')}
                  </Text>
                </View>
                <View style={styles.resourceAction}>
                  <Text style={styles.resourceActionText}>
                    {t('modal.call_now')}
                  </Text>
                </View>
              </Pressable>

              {/* Crisis Text Line */}
              <Pressable
                onPress={handleTextMessage}
                style={styles.resourceButtonSecondary}
                accessibilityRole="button"
                accessibilityLabel={t('modal.text_741741_accessibility')}
                accessibilityHint={t('modal.text_741741_hint')}
                testID="crisis-text-741741"
              >
                <Text style={styles.resourceIcon}>
                  {CRISIS_RESOURCES.text_741741.icon}
                </Text>
                <View style={styles.resourceText}>
                  <Text style={styles.resourceNameSecondary}>
                    {t('modal.resource_text_name')}
                  </Text>
                  <Text style={styles.resourceDescriptionSecondary}>
                    {t('modal.resource_text_description')}
                  </Text>
                </View>
                <View style={styles.resourceActionSecondary}>
                  <Text style={styles.resourceActionTextSecondary}>
                    {t('modal.text_now')}
                  </Text>
                </View>
              </Pressable>

              {/* International Resources */}
              <Pressable
                onPress={handleInternationalLink}
                style={styles.resourceButtonTertiary}
                accessibilityRole="link"
                accessibilityLabel={t('modal.international_accessibility')}
                testID="crisis-international"
              >
                <Text style={styles.resourceIcon}>
                  {CRISIS_RESOURCES.international.icon}
                </Text>
                <View style={styles.resourceText}>
                  <Text style={styles.resourceNameTertiary}>
                    {t('modal.resource_international_name')}
                  </Text>
                  <Text style={styles.resourceDescriptionTertiary}>
                    {t('modal.resource_international_description')}
                  </Text>
                </View>
              </Pressable>
            </View>

            {/* Disclaimer */}
            <View style={styles.disclaimer}>
              <Text style={styles.disclaimerText}>
                {t('modal.disclaimer')}
              </Text>
            </View>

            {/* Close Button */}
            <Pressable
              onPress={onClose}
              style={styles.closeButton}
              accessibilityRole="button"
              accessibilityLabel={t('modal.close_accessibility')}
              testID="crisis-modal-close"
            >
              <Text style={styles.closeButtonText}>
                {t('modal.close')}
              </Text>
            </Pressable>
          </ScrollView>
        </View>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    justifyContent: 'flex-end',
  },
  modalContainer: {
    backgroundColor: '#FFFFFF',
    borderTopLeftRadius: 24,
    borderTopRightRadius: 24,
    maxHeight: '90%',
    minHeight: 400,
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    padding: 24,
    paddingBottom: 40,
  },

  // Header
  header: {
    alignItems: 'center',
    marginBottom: 24,
    paddingBottom: 20,
    borderBottomWidth: 1,
    borderBottomColor: '#E5E7EB',
  },
  headerDefault: {},
  headerUrgent: {
    backgroundColor: '#FEF2F2',
    marginHorizontal: -24,
    marginTop: -24,
    paddingHorizontal: 24,
    paddingTop: 24,
    borderTopLeftRadius: 24,
    borderTopRightRadius: 24,
  },
  headerIcon: {
    fontSize: 48,
    marginBottom: 12,
  },
  headerTitle: {
    fontSize: 22,
    fontWeight: '700',
    color: '#111827',
    textAlign: 'center',
    marginBottom: 8,
  },
  headerSubtitle: {
    fontSize: 15,
    color: '#6B7280',
    textAlign: 'center',
    lineHeight: 22,
  },

  // Resources Section
  resourcesSection: {
    gap: 12,
    marginBottom: 24,
  },

  // Primary Resource (988)
  resourceButtonPrimary: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#DC2626', // red-600
    padding: 16,
    borderRadius: 16,
    minHeight: 72, // Larger touch target for crisis CTA
    ...Platform.select({
      ios: {
        shadowColor: '#DC2626',
        shadowOffset: { width: 0, height: 4 },
        shadowOpacity: 0.3,
        shadowRadius: 8,
      },
      android: {
        elevation: 8,
      },
      web: {
        boxShadow: '0 4px 12px rgba(220, 38, 38, 0.3)',
      },
    }),
  },
  resourceIcon: {
    fontSize: 28,
    marginRight: 12,
  },
  resourceText: {
    flex: 1,
  },
  resourceName: {
    fontSize: 17,
    fontWeight: '700',
    color: '#FFFFFF',
    marginBottom: 2,
  },
  resourceDescription: {
    fontSize: 13,
    color: 'rgba(255, 255, 255, 0.9)',
  },
  resourceAction: {
    backgroundColor: '#FFFFFF',
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 8,
  },
  resourceActionText: {
    fontSize: 14,
    fontWeight: '700',
    color: '#DC2626',
  },

  // Secondary Resource (Text)
  resourceButtonSecondary: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#1E40AF', // blue-800
    padding: 16,
    borderRadius: 16,
    minHeight: 72,
  },
  resourceNameSecondary: {
    fontSize: 17,
    fontWeight: '700',
    color: '#FFFFFF',
    marginBottom: 2,
  },
  resourceDescriptionSecondary: {
    fontSize: 13,
    color: 'rgba(255, 255, 255, 0.9)',
  },
  resourceActionSecondary: {
    backgroundColor: '#FFFFFF',
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 8,
  },
  resourceActionTextSecondary: {
    fontSize: 14,
    fontWeight: '700',
    color: '#1E40AF',
  },

  // Tertiary Resource (International)
  resourceButtonTertiary: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#F3F4F6', // gray-100
    padding: 16,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: '#E5E7EB',
    minHeight: 64,
  },
  resourceNameTertiary: {
    fontSize: 15,
    fontWeight: '600',
    color: '#374151',
    marginBottom: 2,
  },
  resourceDescriptionTertiary: {
    fontSize: 13,
    color: '#6B7280',
  },

  // Disclaimer
  disclaimer: {
    backgroundColor: '#F9FAFB',
    padding: 16,
    borderRadius: 12,
    marginBottom: 20,
  },
  disclaimerText: {
    fontSize: 12,
    color: '#6B7280',
    lineHeight: 18,
    textAlign: 'center',
  },

  // Close Button
  closeButton: {
    paddingVertical: 16,
    paddingHorizontal: 24,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#E5E7EB',
    alignItems: 'center',
    minHeight: 52,
  },
  closeButtonText: {
    fontSize: 16,
    fontWeight: '500',
    color: '#6B7280',
  },
});

export default CrisisResourcesModal;
